/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { ModalScrollScreen } from '../../shared/layout/ModalScrollScreen';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts';
import { typography } from '../../shared/theme/typography';
import { creceSuccessCopy } from '../../../../../shared/plan-crece-copy';
import { usePlanEntitlements } from '../providers/PlanEntitlementsProvider';
import { clearPlanIntent, clearPlanIntentIfSubscribed } from '../feature-flags/planIntentService';
import { trackCreceFunnel } from './trackCreceFunnel';
import { CRECE_FUNNEL_EVENTS } from '../../../../../shared/crece-funnel';
import { resolveSubscribedPaidTier } from './billingScreen.utils';
import { planDisplayName } from '../../../../../shared/billing-catalog';
import { verifyMpSubscription } from './verifyMpSubscription';

function readMpReturnPreapprovalId(): string | undefined {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
  const id = new URLSearchParams(window.location.search).get('preapproval_id');
  return id && /^[a-zA-Z0-9]+$/.test(id) ? id : undefined;
}

function clearMpReturnParamFromUrl(): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('mp') && !url.searchParams.has('preapproval_id')) return;
  url.searchParams.delete('mp');
  url.searchParams.delete('preapproval_id');
  const qs = url.searchParams.toString();
  window.history.replaceState({}, '', `${url.pathname}${qs ? `?${qs}` : ''}${url.hash}`);
}

export function CreceSuccessScreen() {
  const navigation = useNavigation<any>();
  const { entitlements, refresh } = usePlanEntitlements();
  const [confirming, setConfirming] = useState(true);
  const returnCapturedRef = useRef(false);
  const verifiedRef = useRef(false);
  const preapprovalRef = useRef<string | undefined>(undefined);

  const subscribedTier = useMemo(
    () => resolveSubscribedPaidTier(entitlements),
    [entitlements],
  );
  const active = subscribedTier === 'crece' || subscribedTier === 'emprende';
  const planName = planDisplayName(entitlements?.plan_code ?? 'crece');

  useEffect(() => {
    ensureTicketFonts();
    if (!returnCapturedRef.current) {
      returnCapturedRef.current = true;
      preapprovalRef.current = readMpReturnPreapprovalId();
      trackCreceFunnel(CRECE_FUNNEL_EVENTS.payReturn, {
        mp:
          Platform.OS === 'web' && typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search).get('mp')
            : null,
      });
      clearMpReturnParamFromUrl();
    }
    if (verifiedRef.current) return;
    const orgId = (entitlements?.organization_id as string | undefined) ?? null;
    const preapprovalId = preapprovalRef.current;
    if (!orgId && !preapprovalId) return;
    verifiedRef.current = true;
    void verifyMpSubscription(orgId, preapprovalId).finally(() => {
      void refresh({ force: true });
    });
  }, [entitlements?.organization_id, refresh]);

  useEffect(() => {
    if (!entitlements) return;
    void clearPlanIntentIfSubscribed(entitlements);
  }, [entitlements]);

  useEffect(() => {
    if (active) {
      setConfirming(false);
      trackCreceFunnel(CRECE_FUNNEL_EVENTS.creceActive, { plan_code: subscribedTier });
      void clearPlanIntent();
      return;
    }
    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const delays = [2000, 4000, 8000, 16000, 30000];
    const orgId = (entitlements?.organization_id as string | undefined) ?? null;
    const poll = async () => {
      if (cancelled) return;
      await verifyMpSubscription(orgId, preapprovalRef.current);
      if (cancelled) return;
      await refresh({ force: true });
      attempt += 1;
      if (cancelled || attempt >= delays.length) {
        setConfirming(false);
        return;
      }
      timer = setTimeout(poll, delays[attempt]);
    };
    timer = setTimeout(poll, delays[0]);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [active, entitlements?.organization_id, refresh, subscribedTier]);

  const title = active
    ? `El plan ${planName} quedó activo`
    : 'Estamos confirmando tu suscripción';
  const body = active
    ? planName === 'Emprende'
      ? 'Ya tenés más capacidad para ventas, pedidos y turnos desde tu local.'
      : 'Ya podés sumar equipo y sucursales.'
    : 'Si ya pagaste, el plan se activa solo. Podés seguir.';

  return (
    <ModalScrollScreen variant="settingsWide" contentContainerStyle={styles.gap}>
      <View style={styles.card}>
        {confirming && !active ? (
          <ActivityIndicator color={ticketColors.gold} style={{ marginBottom: spacing.md }} />
        ) : active ? (
          <View style={styles.successMark} accessibilityElementsHidden>
            <Text style={styles.successMarkText}>✓</Text>
          </View>
        ) : null}
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.body}>{body}</Text>
      </View>
      <TouchableOpacity
        style={styles.primaryBtn}
        onPress={() => navigation.navigate('MainTabs' as never)}
        accessibilityRole="button"
      >
        <Text style={styles.primaryBtnText}>{creceSuccessCopy.ctaHome}</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.secondaryBtn}
        onPress={() => navigation.navigate('Billing' as never)}
        accessibilityRole="button"
      >
        <Text style={styles.secondaryBtnText}>{creceSuccessCopy.ctaBilling}</Text>
      </TouchableOpacity>
    </ModalScrollScreen>
  );
}

const styles = StyleSheet.create({
  gap: { gap: spacing.md, paddingBottom: spacing.xl },
  card: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  successMark: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: ticketColors.brandSoft,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.xs,
  },
  successMarkText: {
    fontFamily: ticketFonts.sans,
    fontSize: 22,
    fontWeight: '800',
    color: ticketColors.gold,
  },
  title: {
    ...typography.titleLg,
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
    fontWeight: '800',
  },
  body: {
    ...typography.body,
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink2,
    lineHeight: 22,
  },
  primaryBtn: {
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  primaryBtnText: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.goldInk,
    fontWeight: '800',
    fontSize: 16,
  },
  secondaryBtn: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingVertical: spacing.sm,
    alignItems: 'center',
  },
  secondaryBtnText: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
    fontWeight: '700',
  },
});
